/**
 * Définitions des indicateurs — tickets #48 et #45.
 *
 * Le métier ne savait pas à quoi correspondaient « performance globale »,
 * « empathie » ou « conclusion », ni comment le score global d'un audit était
 * obtenu. Les libellés étaient documentés à deux endroits (infobulles des
 * cartes et menu Aide) au risque de diverger : cette source unique alimente
 * désormais les deux.
 *
 * Chaque formule décrit le calcul réellement appliqué dans le code
 * (`gcp/services/audio-pipeline-worker/main.py` pour l'analyse d'un appel,
 * `supabase/functions/audit-calls/index.ts` pour l'audit, la RPC
 * `calculate_global_metrics` pour les taux de la vue d'ensemble).
 *
 * Plan de modernisation UX, §7 : ce dictionnaire est la source commune des
 * libellés, unités et aides des cartes, graphiques, tableaux et fiches. Un
 * écran qui affiche un indicateur doit reprendre `label` et `unit` d'ici
 * plutôt que d'écrire son propre intitulé.
 */

export interface IndicatorDefinition {
  /** Clé stable, utilisée pour les liens depuis les autres écrans. */
  key: string;
  /** Nom affiché dans l'interface. */
  label: string;
  /** Unité affichée à côté de la valeur : « % » pour une part d'appels, « /100 » pour une note. */
  unit?: "%" | "/100" | "appels" | "durée";
  /** Sens favorable d'une évolution ; « neutral » pour le volume ou la durée. */
  direction?: "higher" | "lower" | "neutral";
  /** Phrase courte, pour une infobulle. */
  short: string;
  /** Explication destinée au menu Aide. */
  detail: string;
  /** Mode de calcul, quand il éclaire la lecture du chiffre. */
  formula?: string;
  /** Regroupement dans le menu Aide. */
  group: "Appel" | "Relationnel" | "Procédures" | "Audit IA";
}

export const INDICATOR_DEFINITIONS: IndicatorDefinition[] = [
  // ── Indicateurs d'un appel ────────────────────────────────────────────────
  {
    key: "total_calls",
    label: "Volume d'appels",
    unit: "appels",
    direction: "neutral",
    group: "Appel",
    short: "Nombre d'appels du périmètre filtré.",
    detail:
      "Contexte de lecture des autres indicateurs. Une baisse de volume n'est ni un progrès ni une dégradation en soi : elle se lit avec le motif et la période.",
  },
  {
    key: "average_duration",
    label: "Durée moyenne",
    unit: "durée",
    direction: "neutral",
    group: "Appel",
    short: "Durée moyenne d'un appel du périmètre, en minutes et secondes.",
    detail:
      "Moyenne de la durée enregistrée des appels. Une durée plus courte n'est pas nécessairement un progrès : un appel écourté peut produire une réitération.",
    formula: "moyenne de la durée des appels (une durée absente compte pour zéro)",
  },
  {
    key: "performance",
    label: "Qualité relationnelle",
    unit: "/100",
    direction: "higher",
    group: "Appel",
    short: "Moyenne des quatre compétences relationnelles évaluées sur l'appel (note sur 100).",
    detail:
      "Note de synthèse de la conduite de l'appel par l'agent, anciennement « performance globale » ou « Agents ». Elle ne mesure ni la satisfaction estimée du client ni le respect des procédures métier, qui font l'objet d'indicateurs distincts. « Qualité relationnelle 70/100 » signifie : la moyenne des quatre compétences relationnelles, sur les appels de la période filtrée, est de 70 sur 100.",
    formula: "partie entière de la moyenne des scores accueil, empathie, qualité du traitement et conclusion (chacun de 0 à 100)",
  },
  {
    key: "satisfaction_rate",
    label: "Satisfaction estimée par l'IA",
    unit: "%",
    direction: "higher",
    group: "Appel",
    short: "Part des appels où l'IA estime le client satisfait ou très satisfait.",
    detail:
      "Il s'agit d'une part d'appels, pas d'un score moyen : 62 % signifie que 62 appels sur 100 sont estimés « satisfait » ou « très satisfait » à partir du verbatim. Les appels sans estimation comptent au dénominateur, ce qui tire le taux vers le bas lorsque l'analyse est incomplète.",
    formula: "100 × appels estimés satisfaits ou très satisfaits (≥ 4 sur 5) ÷ appels du périmètre",
  },
  {
    key: "satisfaction",
    label: "Satisfaction estimée",
    group: "Appel",
    unit: "/100",
    direction: "higher",
    short: "Ressenti du client déduit de la conversation par l'IA, de très insatisfait à très satisfait.",
    detail:
      "L'IA qualifie le ressenti exprimé par le client pendant l'échange, sur cinq niveaux. Il s'agit d'une estimation à partir du verbatim, non d'une enquête déclarative : un client peut être satisfait de la réponse tout en restant mécontent de la situation initiale.",
    formula:
      "cinq niveaux ramenés à une note sur 100 dans les moyennes : très satisfait ou satisfait = 100, neutre = 50, insatisfait ou très insatisfait = 0",
  },
  {
    key: "resolution_status",
    label: "Taux de résolution",
    unit: "%",
    direction: "higher",
    group: "Appel",
    short: "Part des appels dont la demande a été entièrement traitée pendant l'échange.",
    detail:
      "Un appel est classé « Résolu complètement », « Résolu partiellement », « Non résolu » ou « Transféré vers un autre service ». Le taux de résolution ne retient que les appels complètement résolus ; un transfert n'est donc pas compté comme une résolution. Il porte sur l'issue de l'appel ; la « qualité du traitement » note la manière : un appel résolu peut obtenir 70/100.",
    formula: "100 × appels « Résolu complètement » ÷ appels du périmètre",
  },
  {
    key: "reiteration",
    label: "Réitération",
    unit: "%",
    direction: "lower",
    group: "Appel",
    short: "Part des appels où le client indique recontacter pour une demande déjà abordée.",
    detail:
      "Première, deuxième, ou troisième itération et plus. La réitération est déduite du verbatim par l'IA (le client dit rappeler, mentionne un échange précédent) ; elle n'est pas calculée en rapprochant des contacts reliés et ne repose sur aucune fenêtre de temps fixe. Elle signale un motif qui n'a pas été traité au premier contact : c'est un indicateur d'effort client, à lire avec le taux de résolution.",
    formula: "100 × appels en deuxième itération ou plus ÷ appels du périmètre",
  },
  {
    key: "pain_point",
    label: "Appels avec un irritant identifié",
    unit: "%",
    direction: "lower",
    group: "Appel",
    short: "Irritant principal rencontré par le client durant l'appel.",
    detail:
      "L'IA retient l'irritant dominant de l'échange parmi la liste définie dans les réglages du projet (attente, incompréhension, procédure inadaptée…). Un appel peut être résolu tout en présentant un irritant. À vérifier avant toute présentation agrégée : la règle qui distingue « aucun irritant » d'une valeur absente, et le dénominateur retenu — un taux de 100 % répété sur plusieurs motifs signale plus probablement une valeur toujours renseignée qu'une réalité métier.",
    formula:
      "100 × appels dont le point de friction est renseigné (non vide) ÷ appels du périmètre — une valeur du type « aucun » est aujourd'hui comptée comme un irritant",
  },

  // ── Compétences relationnelles ────────────────────────────────────────────
  {
    key: "accueil_score",
    label: "Accueil",
    unit: "/100",
    direction: "higher",
    group: "Relationnel",
    short: "Qualité de la prise de contact : présentation, identification, mise en confiance.",
    detail:
      "Évalue l'ouverture de l'appel : l'agent se présente, identifie son interlocuteur, annonce clairement le cadre de l'échange et adopte un ton adapté dès les premières secondes.",
    formula: "note de 0 à 100 attribuée par l'IA sur le début de la conversation",
  },
  {
    key: "empathie_score",
    label: "Empathie",
    unit: "/100",
    direction: "higher",
    group: "Relationnel",
    short: "Capacité à reconnaître la situation du client et à en tenir compte.",
    detail:
      "Mesure l'écoute active : reformulation de la demande, reconnaissance explicite du désagrément subi, absence de réponse purement procédurale face à un client contrarié. Une réponse techniquement juste mais indifférente au contexte obtient une note basse.",
    formula: "note de 0 à 100 attribuée par l'IA sur l'ensemble de la conversation",
  },
  {
    key: "resolution_score",
    label: "Qualité du traitement",
    unit: "/100",
    direction: "higher",
    group: "Relationnel",
    short: "Conduite du traitement de la demande, indépendamment de son issue (compétence « résolution »).",
    detail:
      "Évalue la conduite du traitement : questions utiles posées, exactitude de l'information donnée, actions engagées, absence de renvoi inutile. À distinguer du taux de résolution, qui porte sur l'issue de l'appel et non sur la manière.",
    formula: "note de 0 à 100 attribuée par l'IA",
  },
  {
    key: "conclusion_score",
    label: "Conclusion",
    unit: "/100",
    direction: "higher",
    group: "Relationnel",
    short: "Qualité de la clôture : récapitulatif, prochaines étapes, formule de fin.",
    detail:
      "Évalue la fin de l'échange : l'agent récapitule ce qui a été décidé, annonce les suites et leur délai, vérifie qu'il ne reste pas de question, puis clôt par une formule adaptée. Un appel qui se termine sans que le client sache ce qui va se passer obtient une note basse.",
    formula: "note de 0 à 100 attribuée par l'IA sur la fin de la conversation",
  },

  // ── Procédures métier ─────────────────────────────────────────────────────
  {
    key: "procedure_score",
    label: "Procédures métier",
    unit: "/100",
    direction: "higher",
    group: "Procédures",
    short: "Part des consignes métier attendues sur cet appel qui ont été respectées.",
    detail:
      "Pour chaque appel, l'IA contrôle les procédures applicables : celles du tronc commun, qui valent pour tous les appels, et celles rattachées au motif identifié. Le score est la part des points obtenus sur les points contrôlables. Il ne dépend donc pas du nombre total de procédures définies dans le projet, mais de celles qui concernaient cet appel.",
    formula: "100 × points des procédures respectées ÷ points des procédures contrôlées",
  },
  {
    key: "procedures_compliance_rate",
    label: "Taux de conformité",
    unit: "%",
    direction: "higher",
    group: "Procédures",
    short: "Part des contrôles de procédure conformes sur l'ensemble du périmètre filtré.",
    detail:
      "Agrège tous les contrôles de procédure des appels du périmètre, et non la moyenne des scores par appel : un appel comportant davantage de procédures contrôlées y pèse donc davantage. C'est la lecture pertinente pour comparer des procédures entre elles.",
    formula: "100 × points respectés ÷ points contrôlés, sur tous les appels filtrés",
  },
  {
    key: "procedures_common",
    label: "Tronc commun",
    group: "Procédures",
    short: "Procédures attendues sur tous les appels, quel que soit le motif.",
    detail:
      "Une procédure sans motif de rattachement s'applique à chaque appel (identification de l'abonné, vérification des coordonnées, phrase de clôture…). Elle est donc contrôlée sur l'intégralité du périmètre, ce qui en fait l'indicateur le plus comparable dans le temps.",
  },
  {
    key: "procedures_specific",
    label: "Procédures spécifiques",
    group: "Procédures",
    short: "Procédures attendues seulement sur certains motifs d'appel.",
    detail:
      "Une procédure rattachée à un motif (parent, éventuellement enfant) n'est contrôlée que sur les appels de ce motif. Son taux se lit toujours au regard du nombre de contrôles : un taux bas sur quelques dizaines d'appels n'a pas la même portée qu'un taux bas sur plusieurs milliers.",
  },

  // ── Audit IA ──────────────────────────────────────────────────────────────
  {
    key: "audit_global_score",
    label: "Score global de l'audit",
    group: "Audit IA",
    short: "Moyenne des composantes évaluées sur les appels analysés par l'audit.",
    detail:
      "Ce score agrège les cinq composantes disponibles sur l'échantillon audité : accueil, empathie, résolution, conclusion et procédures métier. Chaque composante est d'abord moyennée sur les seuls appels où elle est renseignée, puis les composantes disponibles sont moyennées entre elles. C'est ce qui explique qu'un calcul refait à la main depuis les pourcentages de la section « Données & faits » puisse s'en écarter : les dénominateurs diffèrent d'une composante à l'autre, et une composante absente est ignorée au lieu de compter pour zéro.",
    formula:
      "moyenne des moyennes disponibles parmi accueil, empathie, résolution, conclusion et procédures métier",
  },
  {
    key: "audit_calls_analyzed",
    label: "Appels analysés",
    group: "Audit IA",
    short: "Nombre d'appels réellement transmis à l'IA pour produire l'audit.",
    detail:
      "Un audit porte sur un échantillon : les 100 appels les plus récents du périmètre filtré pour les statistiques agrégées ; s'y ajoutent les 20 appels du périmètre dont la satisfaction estimée est la plus basse, parmi lesquels au plus 15 transcriptions disponibles sont lues en entier (chemin vérifié dans `supabase/functions/audit-calls`). Les indicateurs de l'audit décrivent donc cet échantillon, pas l'intégralité de la population filtrée ; un examen orienté vers les appels difficiles ne permet pas d'extrapoler à toute la population.",
  },
];

const BY_KEY = new Map(INDICATOR_DEFINITIONS.map((d) => [d.key, d]));

export const getIndicator = (key: string): IndicatorDefinition | undefined => BY_KEY.get(key);

/** Libellé d'un indicateur, avec repli si la clé est inconnue. */
export const indicatorLabel = (key: string, fallback = key): string => BY_KEY.get(key)?.label ?? fallback;

/** Échantillonnage réel de l'audit, à exposer avant le lancement et dans le rapport. */
export const AUDIT_SAMPLE = {
  recentCalls: 100,
  focusCalls: 20,
  maxTranscripts: 15,
} as const;

/** Correspondances libellé affiché → définition, pour les composants génériques. */
const LABEL_MATCHERS: Array<[RegExp, string]> = [
  [/tronc commun/i, "procedures_common"],
  [/proc[éd]dures? sp[ée]cifiques?/i, "procedures_specific"],
  [/conformit[ée]/i, "procedures_compliance_rate"],
  [/proc[ée]dure/i, "procedure_score"],
  [/score global/i, "audit_global_score"],
  [/appels analys[ée]s/i, "audit_calls_analyzed"],
  [/qualit[ée] relationnelle|performance/i, "performance"],
  [/qualit[ée] du traitement/i, "resolution_score"],
  [/taux de satisfaction|satisfaction estim[ée]e par/i, "satisfaction_rate"],
  [/satisfaction/i, "satisfaction"],
  [/r[ée]solution/i, "resolution_status"],
  [/r[ée]it[ée]ration/i, "reiteration"],
  [/friction|irritant/i, "pain_point"],
  [/volume/i, "total_calls"],
  [/dur[ée]e/i, "average_duration"],
  [/accueil/i, "accueil_score"],
  [/empathie/i, "empathie_score"],
  [/conclusion/i, "conclusion_score"],
];

/** Définition la plus pertinente pour un libellé d'indicateur affiché. */
export function findIndicatorByLabel(label: string): IndicatorDefinition | undefined {
  for (const [pattern, key] of LABEL_MATCHERS) {
    if (pattern.test(label)) return BY_KEY.get(key);
  }
  return undefined;
}
